import {
  currentBusinessModeLabel,
  isStableBusinessModeCode,
  requireActiveBusinessMode,
} from './businessModes';
import { freezeSelectedCalculationFacts } from './calculationLifecycle';
import { freezePublicQuotationProjections } from './frozenQuotationProjection';
import { createQuoteWorkflowVersion } from './machine';
import { quoteOutputBusinessSections } from './outputTerms';
import {
  activeRoleIdForState,
  canUseActivePermissionAction,
  quotePermissionContext,
  resolveDisposablePermissionAction,
} from './permissionPolicy';
import { isReceiptCapableShareItem } from './publicShareSnapshot';
import {
  buildQuoteChannelProjection,
  shareFieldCombinationSnapshot,
} from './quoteChannelProjection';
import { quoteAmounts } from './quoteTotals';
import {
  assertShareFieldCombinationUsable,
  shareFieldCombinationForChannel,
} from './shareExportFieldCombinations';
import {
  createShareStyleSnapshot,
  isKnownShareStyleSnapshot,
  resolveEnabledShareStyle,
  sameShareStyleSnapshotFacts,
} from './shareStyles';
import type {
  CpqState,
  CustomerReceiptAction,
  FrozenCustomerSnapshot,
  NegotiationLink,
  PublicShareItemSnapshot,
  Quote,
  QuoteColumnSnapshot,
  QuoteItem,
  QuotePublication,
  ReceiptDisposition,
  ReceiptRecord,
  ReceiptSelectionItemSnapshot,
  ShareRecord,
} from './types';
import type { UnifiedFieldValuePort } from './unifiedFieldPort';

export interface ReceiptAccess {
  canView: boolean;
  canConvert: boolean;
}

interface ReceiptBinding {
  receipt: ReceiptRecord;
  share?: ShareRecord;
  sourceQuoteKey?: string;
  sourceQuote?: Quote;
  valid: boolean;
}

export interface SubmitShareReceiptInput {
  shareId: string;
  companyOrName: string;
  phone: string;
  note: string;
  items?: PublicShareItemSnapshot[];
  overallDiscount?: number;
  createdAt?: string;
  linkId?: string;
  publicationId?: string;
  customerAction?: CustomerReceiptAction;
}

export interface NegotiationLinkIdentity {
  subjectId: string;
  skinId: string;
  subjectLabel: string;
  skinLabel: string;
}

const defaultLinkIdentity: NegotiationLinkIdentity = {
  subjectId: 'default-subject',
  skinId: 'default-skin',
  subjectLabel: '默认公司主体',
  skinLabel: '默认皮肤',
};

function linkIdentity(
  input?: Partial<NegotiationLinkIdentity>,
): NegotiationLinkIdentity {
  return { ...defaultLinkIdentity, ...input };
}

function quoteMasterId(quote: Quote): string {
  return quote.masterId ?? quote.quoteId;
}

function assertExternalShareAllowed(state: CpqState, quote: Quote): void {
  const roleId = activeRoleIdForState(state);
  if (
    !roleId ||
    !canUseActivePermissionAction(
      state,
      'customers',
      'customers:customer.read',
    ) ||
    !resolveDisposablePermissionAction(
      state,
      roleId,
      'quotes',
      'quotes:quote.share.external',
      quote.status === 'draft' ? 'draft' : 'submitted',
      quotePermissionContext(state, quote),
    )
  )
    throw new Error('当前成员无权管理客户固定链接');
}

function quoteForLink(
  state: CpqState,
  link: NonNullable<CpqState['negotiationLinks']>[number],
): Quote {
  const publication = link.currentPublicationId
    ? state.publications?.find((item) => item.id === link.currentPublicationId)
    : undefined;
  const quote = publication ? state.quotes[publication.quoteId] : undefined;
  if (!quote || quoteMasterId(quote) !== link.quoteMasterId)
    throw new Error('客户链接缺少精确报价绑定');
  return quote;
}

export function isValidNegotiationExpiry(value: string): boolean {
  return (
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z$/.test(value) &&
    Number.isFinite(new Date(value).getTime())
  );
}

export function negotiationEffectiveExpiry(
  link: Pick<NegotiationLink, 'expiresAt'>,
  publication: Pick<QuotePublication, 'validUntilSnapshot'> | undefined,
): string | undefined {
  return (
    link.expiresAt ??
    (publication?.validUntilSnapshot
      ? `${publication.validUntilSnapshot}T23:59:59.999`
      : undefined)
  );
}

export function negotiationIsExpired(
  link: Pick<NegotiationLink, 'expiresAt'>,
  publication: Pick<QuotePublication, 'validUntilSnapshot'> | undefined,
  now = Date.now(),
): boolean {
  const expiry = negotiationEffectiveExpiry(link, publication);
  return Boolean(
    expiry &&
      Number.isFinite(new Date(expiry).getTime()) &&
      new Date(expiry).getTime() < now,
  );
}

function publicationSource(state: CpqState, publicationId: string) {
  const publication = state.publications?.find(
    (item) => item.id === publicationId,
  );
  const link = publication
    ? state.negotiationLinks?.find((item) => item.id === publication.linkId)
    : undefined;
  return { publication, link };
}

export function validateNegotiationPublicationInput(input: {
  customerName: string;
  customerPhone: string;
  validUntil: string;
}): { customerName?: string; phone?: string; validUntil?: string } {
  return {
    ...(!input.customerName.trim() ? { customerName: '请输入客户名称' } : {}),
    ...(!/^1\d{10}$/.test(input.customerPhone)
      ? { phone: '请输入正确的联系电话' }
      : {}),
    ...(!input.validUntil
      ? { validUntil: '请先在报价设置中填写报价有效期' }
      : {}),
  };
}

/** Freeze a new immutable publication and reuse the one master-level URL. */
export function publishNegotiation(
  state: CpqState,
  quoteId: string,
  fieldValuePort: UnifiedFieldValuePort,
  input: {
    customerId: string;
    customerName: string;
    customerPhone: string;
    allowSelection: boolean;
    expiresAt?: string | null;
    snapshotPolicy: 'published-channel-v1';
    /** Compatibility assertions only. The service derives the persisted snapshot. */
    customerSnapshot?: FrozenCustomerSnapshot;
    validUntilSnapshot?: string;
    itemsSnapshot?: PublicShareItemSnapshot[];
    overallDiscountSnapshot?: number;
    publicFieldSnapshot?: QuoteColumnSnapshot[];
    linkIdentity?: Partial<NegotiationLinkIdentity>;
    /** Compatibility guard: PR73 uses one fixed link and rejects rotation. */
    regenerate?: boolean;
    /** Unified output combination selected for the immutable publication. */
    combinationId?: string;
    /** Independent enabled style selected for the immutable publication. */
    shareStyleId?: string;
  },
): CpqState {
  const quote = state.quotes[quoteId];
  if (!quote) throw new Error('报价不存在，不能发布');
  if (!isStableBusinessModeCode(quote.businessModeCode))
    throw new Error('旧业务模式不能发布，必须先选择启用的 catalog 业务模式');
  requireActiveBusinessMode(
    state.catalog.businessModes,
    quote.businessModeCode,
  );
  const validation = validateNegotiationPublicationInput({
    customerName: input.customerName,
    customerPhone: input.customerPhone,
    validUntil: quote.validUntil,
  });
  if (Object.values(validation).some(Boolean))
    throw new Error(Object.values(validation).filter(Boolean).join('；'));
  assertExternalShareAllowed(state, quote);
  if (quote.status === 'void' || quote.status === 'archived')
    throw new Error('当前报价状态不能发布');
  if (
    typeof input.expiresAt === 'string' &&
    !isValidNegotiationExpiry(input.expiresAt)
  )
    throw new Error('有效期必须是可比较的 ISO 时间');
  const selectedCombination = input.combinationId
    ? assertShareFieldCombinationUsable(
        state,
        quoteId,
        input.combinationId,
        'channel:public-link',
      ).combination
    : shareFieldCombinationForChannel(state, 'channel:public-link');
  if (!selectedCombination) throw new Error('没有启用的客户链接分享字段组合');
  const projection = buildQuoteChannelProjection(
    state,
    quoteId,
    'channel:public-link',
    fieldValuePort,
    selectedCombination.fieldIds,
  );
  // The publication keeps an internal, channel-approved receipt payload for
  // reversible customer selection.  It is never used to decide visible
  // columns: the frozen quotation-list DTO below is built only from the
  // selected combination projection.
  const receiptProjection = buildQuoteChannelProjection(
    state,
    quoteId,
    'channel:public-link',
    fieldValuePort,
  );
  const combinationSnapshot = shareFieldCombinationSnapshot(
    state,
    selectedCombination,
    projection,
  );
  if (
    !projection.customerSnapshot ||
    projection.validUntilSnapshot === undefined ||
    projection.overallDiscountSnapshot === undefined ||
    !receiptProjection.customerSnapshot ||
    receiptProjection.validUntilSnapshot === undefined ||
    receiptProjection.overallDiscountSnapshot === undefined
  )
    throw new Error('公开链接投影缺少必需冻结事实');
  const frozenQuotation = freezePublicQuotationProjections(
    state,
    projection,
    combinationSnapshot,
  );
  if (input.customerId !== projection.customerSnapshot.customerId) {
    throw new Error('发布客户必须绑定当前报价的客户目录记录');
  }
  if (
    input.customerName !== projection.customerSnapshot.name ||
    input.customerPhone !== projection.customerSnapshot.phone
  ) {
    throw new Error('发布客户名称或电话与客户目录记录不一致');
  }
  const same = (left: unknown, right: unknown) =>
    JSON.stringify(left) === JSON.stringify(right);
  const assertedSnapshots = [
    [input.customerSnapshot, projection.customerSnapshot],
    [input.validUntilSnapshot, projection.validUntilSnapshot],
    [input.itemsSnapshot, receiptProjection.publicItems],
    [input.overallDiscountSnapshot, receiptProjection.overallDiscountSnapshot],
    [input.publicFieldSnapshot, receiptProjection.publicFieldSnapshot],
  ] as const;
  if (
    assertedSnapshots.some(
      ([requested, derived]) =>
        requested !== undefined && !same(requested, derived),
    )
  ) {
    throw new Error('调用者提交的发布快照超出统一通道投影');
  }
  if (input.allowSelection && receiptProjection.selectionOptions.length === 0) {
    throw new Error('选配所需字段或通道不足，未生成分享');
  }
  const masterId = quoteMasterId(quote);
  const next = structuredClone(state);
  next.negotiationLinks ??= [];
  next.publications ??= [];
  const identity = linkIdentity(input.linkIdentity);
  if (input.regenerate) {
    throw new Error(
      '报价固定链接不可重新生成 token，请使用原链接续期或重新启用',
    );
  }
  let link = next.negotiationLinks.find(
    (item) => item.quoteMasterId === masterId,
  );
  if (
    link &&
    (link.subjectId !== identity.subjectId || link.skinId !== identity.skinId)
  ) {
    throw new Error('一个报价主单只能使用一个固定链接主体，禁止静默切换');
  }
  const canonicalToken = link
    ? link.token
    : `cpq-v2-${masterId.toLowerCase()}-${String(next.nextSequence).padStart(4, '0')}`;
  if (!link) {
    const id = `LINK-${String(next.nextSequence).padStart(4, '0')}`;
    link = {
      id,
      token: canonicalToken,
      quoteMasterId: masterId,
      ...identity,
      customerId: projection.customerSnapshot.customerId,
      customerName: input.customerName,
      customerPhone: input.customerPhone,
      allowSelection: input.allowSelection,
      lifecycle: 'active',
      expiresAt: input.expiresAt,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    next.nextSequence += 1;
    next.negotiationLinks.unshift(link);
  }
  if (link.lifecycle !== 'active')
    throw new Error('链接未激活，不能发布新版本');
  if (link.customerId !== projection.customerSnapshot.customerId) {
    throw new Error('固定链接已绑定其他客户，禁止静默换绑');
  }
  const duplicate = next.publications.find(
    (item) =>
      item.linkId === link.id &&
      item.quoteId === quote.quoteId &&
      item.quoteVersion === quote.version,
  );
  const publicationCreatedAt = new Date().toISOString();
  const shareStyleSnapshot = createShareStyleSnapshot(
    resolveEnabledShareStyle(state, input.shareStyleId),
    publicationCreatedAt,
  );
  if (duplicate) {
    if (!isKnownShareStyleSnapshot(state, duplicate.shareStyleSnapshot)) {
      throw new Error('当前报价版本已冻结非法分享样式，不能继续发布');
    }
    if (
      input.shareStyleId !== undefined &&
      !sameShareStyleSnapshotFacts(
        duplicate.shareStyleSnapshot,
        shareStyleSnapshot,
      )
    ) {
      throw new Error('当前报价版本已冻结其他分享样式，不能静默覆盖');
    }
    if (
      input.combinationId !== undefined &&
      JSON.stringify(duplicate.combinationSnapshot) !==
        JSON.stringify(combinationSnapshot)
    ) {
      throw new Error('当前报价版本已冻结其他分享字段组合，不能静默覆盖');
    }
    if (
      JSON.stringify(duplicate.quotationListProjection) !==
        JSON.stringify(frozenQuotation.quotationListProjection) ||
      JSON.stringify(duplicate.headerProjection) !==
        JSON.stringify(frozenQuotation.headerProjection)
    ) {
      throw new Error('当前报价版本冻结报价清单不一致，不能静默覆盖');
    }
    return next;
  }
  link.customerId = projection.customerSnapshot.customerId;
  link.customerName = projection.customerSnapshot.name;
  link.customerPhone = projection.customerSnapshot.phone;
  link.allowSelection = input.allowSelection;
  if (input.expiresAt !== undefined) link.expiresAt = input.expiresAt;
  const id = `PUB-${String(next.nextSequence).padStart(4, '0')}`;
  const businessSections = quoteOutputBusinessSections(quote, state);
  const publication = {
    id,
    linkId: link.id,
    quoteMasterId: masterId,
    quoteId: quote.quoteId,
    quoteVersion: quote.version,
    snapshotPolicy: input.snapshotPolicy,
    customerSnapshot: structuredClone(projection.customerSnapshot),
    validUntilSnapshot: projection.validUntilSnapshot,
    businessModeLabelSnapshot: currentBusinessModeLabel(
      next.catalog.businessModes,
      quote.businessModeCode,
      quote.businessModeLabelSnapshot,
    ),
    itemsSnapshot: structuredClone(receiptProjection.publicItems),
    overallDiscountSnapshot: receiptProjection.overallDiscountSnapshot,
    publicFieldSnapshot: structuredClone(receiptProjection.publicFieldSnapshot),
    placementRowsSnapshot: structuredClone(receiptProjection.placementRows),
    selectionOptionsSnapshot: input.allowSelection
      ? structuredClone(receiptProjection.selectionOptions)
      : [],
    combinationSnapshot,
    quotationListProjection: frozenQuotation.quotationListProjection,
    headerProjection: frozenQuotation.headerProjection,
    ...(businessSections.length > 0
      ? { businessSectionsSnapshot: businessSections }
      : {}),
    allowSelection: link.allowSelection,
    shareStyleSnapshot,
    createdAt: publicationCreatedAt,
  };
  next.nextSequence += 1;
  next.publications.unshift(publication);
  link.currentPublicationId = id;
  link.updatedAt = publication.createdAt;
  // 导入只是把客户回执映射到销售草稿；只有该草稿真正发布新版本后，
  // 本轮回执才完成处理。沿用 sourceReceiptId/convertedQuoteId，避免平行状态。
  next.receipts = next.receipts.map((receipt) =>
    receipt.convertedQuoteId === quote.quoteId ||
    quote.sourceReceiptId === receipt.id
      ? {
          ...receipt,
          disposition: 'adopted' as const,
          dispositionAt: publication.createdAt,
        }
      : receipt,
  );
  next.audits.unshift({
    id: `AUD-${String(next.nextSequence).padStart(4, '0')}`,
    quoteId: quote.quoteId,
    quoteVersion: quote.version,
    action: 'negotiation.published',
    result: 'success',
    detail: `固定链接 ${link.id} 发布 ${id}`,
    createdAt: publication.createdAt,
  });
  next.nextSequence += 1;
  return next;
}

export function setNegotiationLinkLifecycle(
  state: CpqState,
  linkId: string,
  lifecycle: 'active' | 'paused' | 'revoked' | 'expired',
  expiresAt?: string | null,
): CpqState {
  const link = state.negotiationLinks?.find((item) => item.id === linkId);
  if (!link) throw new Error('客户链接不存在');
  assertExternalShareAllowed(state, quoteForLink(state, link));
  if (typeof expiresAt === 'string' && !isValidNegotiationExpiry(expiresAt))
    throw new Error('有效期必须是可比较的 ISO 时间');
  const next = structuredClone(state);
  const target = next.negotiationLinks?.find((item) => item.id === linkId);
  if (!target) throw new Error('客户链接不存在');
  target.lifecycle = lifecycle;
  target.expiresAt = expiresAt === undefined ? target.expiresAt : expiresAt;
  target.updatedAt = new Date().toISOString();
  return next;
}

/** Re-enable the existing fixed link without rotating its token. */
export function reactivateNegotiationLink(
  state: CpqState,
  linkId: string,
  expiresAt?: string | null,
): CpqState {
  const previous = state.negotiationLinks?.find((item) => item.id === linkId);
  if (previous?.lifecycle !== 'revoked') {
    throw new Error('仅已撤销链接可以重新启用');
  }
  return setNegotiationLinkLifecycle(
    state,
    linkId,
    'active',
    expiresAt === undefined ? previous.expiresAt : expiresAt,
  );
}

export function disposeReceipt(
  state: CpqState,
  receiptId: string,
  disposition: ReceiptDisposition,
  note = '',
): CpqState {
  const receipt = requireReceipt(state, receiptId);
  if (receipt.disposition) return state;
  if (!receipt.publicationId || !receipt.linkId || !receipt.quoteMasterId)
    throw new Error('回执不是精确发布绑定，禁止处置');
  if (disposition === 'adopted' && receipt.customerAction !== 'request_change')
    throw new Error('仅“要求修改”的当前回执可以采纳为新草稿');
  assertReceiptPermission(state, receipt, 'quotes:quote.edit');
  const { publication, link } = publicationSource(state, receipt.publicationId);
  if (
    !publication ||
    !link ||
    publication.linkId !== receipt.linkId ||
    publication.quoteMasterId !== receipt.quoteMasterId
  )
    throw new Error('回执来源错绑，禁止处置');
  if (link.currentPublicationId !== publication.id)
    throw new Error('来源发布已过旧，禁止处置');
  const next = structuredClone(state);
  const current = requireReceipt(next, receiptId);
  if (
    disposition === 'adopted' &&
    current.selectionSnapshot.kind === 'structured'
  ) {
    current.selectionSnapshot.items = current.selectionSnapshot.items.map(
      (item) => ({
        ...item,
        disposition: item.selectionKind === 'removed' ? 'adopted' : 'adopted',
      }),
    );
  }
  current.disposition = disposition;
  current.dispositionAt = new Date().toISOString();
  current.dispositionNote = note;
  if (disposition === 'adopted')
    return convertReceiptToQuoteVersion(next, receiptId);
  return next;
}

export function setReceiptItemDisposition(
  state: CpqState,
  receiptId: string,
  itemId: string,
  disposition: 'adopted' | 'rejected' | 'pending',
): CpqState {
  const receipt = requireReceipt(state, receiptId);
  if (
    receipt.status !== 'submitted' ||
    receipt.disposition ||
    receipt.selectionSnapshot.kind !== 'structured'
  ) {
    throw new Error('回执已处理，不能修改逐项决定');
  }
  assertReceiptPermission(state, receipt, 'quotes:quote.edit');
  const target = receipt.selectionSnapshot.items.find(
    (item) =>
      (item.effectiveItem ?? item.originalItemSnapshot)?.itemId === itemId,
  );
  if (!target) throw new Error('回执变更项不存在');
  const next = structuredClone(state);
  const current = requireReceipt(next, receiptId);
  if (current.selectionSnapshot.kind !== 'structured') return state;
  current.selectionSnapshot.items = current.selectionSnapshot.items.map(
    (item) =>
      (item.effectiveItem ?? item.originalItemSnapshot)?.itemId === itemId
        ? { ...item, disposition }
        : item,
  );
  return next;
}

function requireValidCustomerSelection(item: PublicShareItemSnapshot): void {
  if (
    !item.itemId ||
    !Number.isFinite(item.unitPrice) ||
    item.unitPrice < 0 ||
    !Number.isFinite(item.discount) ||
    item.discount < 0 ||
    !Number.isFinite(item.quantity) ||
    item.quantity <= 0 ||
    (item.remark !== undefined && typeof item.remark !== 'string')
  ) {
    throw new Error(`回执选配项 ${item.itemId || '(missing)'} 无效`);
  }
}

function receiptPricingSnapshot(
  item: PublicShareItemSnapshot,
): NonNullable<QuoteItem['pricingBaseSnapshot']> {
  return item.pricingBaseSnapshot
    ? structuredClone(item.pricingBaseSnapshot)
    : {
        kind: 'legacy-unclassified',
        amount: item.unitPrice,
        calculationVersion: 'pricing-v2',
      };
}

function trustedCatalogItem(
  _state: CpqState,
  share: ShareRecord,
  item: PublicShareItemSnapshot,
): QuoteItem {
  const option = share.selectionOptionsSnapshot?.find(
    (candidate) => candidate.deviceId === item.deviceId,
  );
  if (!option)
    throw new Error(`新增设备 ${item.deviceId} 不在发布时冻结的选配上限内`);
  return {
    itemId: item.itemId,
    deviceId: option.deviceId ?? '',
    name: option.name,
    sku: option.sku,
    quantity: item.quantity,
    unitPrice: option.unitPrice,
    discount: option.discount,
    pricingBaseSnapshot: receiptPricingSnapshot(option),
    packageItemPriceSnapshot: option.packageItemPriceSnapshot,
    planId: option.planId,
    planDiscountSnapshot: option.planDiscountSnapshot,
    stock: option.stock ?? 0,
    remark: item.remark,
    sourceType: 'receipt',
    sourceId: share.id,
    sourceName: '第三方新增',
  };
}

function freezeSelectionItems(
  state: CpqState,
  share: ShareRecord,
  items: PublicShareItemSnapshot[],
): ReceiptSelectionItemSnapshot[] {
  const seen = new Set<string>();
  const selected: ReceiptSelectionItemSnapshot[] = items.map((item) => {
    requireValidCustomerSelection(item);
    if (seen.has(item.itemId))
      throw new Error(`回执选配项 ${item.itemId} 重复`);
    seen.add(item.itemId);
    const publicOriginal = share.itemsSnapshot?.find(
      (snapshot) => snapshot.itemId === item.itemId,
    );
    if (publicOriginal && !isReceiptCapableShareItem(publicOriginal)) {
      throw new Error('公开通道未冻结完整选配来源，禁止从目录补齐');
    }
    const original =
      publicOriginal && isReceiptCapableShareItem(publicOriginal)
        ? publicOriginal
        : undefined;
    const trustedBaseRaw = original
      ? structuredClone(original)
      : trustedCatalogItem(state, share, item);
    const trustedBase: QuoteItem = {
      ...trustedBaseRaw,
      deviceId: trustedBaseRaw.deviceId ?? '',
      sourceType: trustedBaseRaw.sourceType ?? 'receipt',
      sourceId: trustedBaseRaw.sourceId ?? share.id,
      sourceName: trustedBaseRaw.sourceName ?? '冻结公开选配',
      stock: trustedBaseRaw.stock ?? 0,
      pricingBaseSnapshot: receiptPricingSnapshot(trustedBaseRaw),
    };
    const originalItemSnapshot = original
      ? structuredClone(original)
      : item.replacementDeviceId
        ? structuredClone(trustedBase)
        : null;
    if (!item.replacementDeviceId) {
      return {
        selectionKind: original ? 'kept' : 'added',
        effectiveItem: {
          ...trustedBase,
          quantity: item.quantity,
          remark: item.remark,
          replacementDeviceId: undefined,
        },
        originalItemSnapshot,
        disposition: 'pending' as const,
      } satisfies ReceiptSelectionItemSnapshot;
    }
    const replacement = share.selectionOptionsSnapshot?.find(
      (device) => device.deviceId === item.replacementDeviceId,
    );
    if (!replacement) {
      throw new Error(
        `替换设备 ${item.replacementDeviceId} 不存在，不能提交回执`,
      );
    }
    const effectiveItem: QuoteItem = {
      ...trustedBase,
      deviceId: replacement.deviceId ?? '',
      name: replacement.name,
      sku: replacement.sku,
      quantity: item.quantity,
      unitPrice: replacement.unitPrice,
      discount: trustedBase.discount,
      pricingBaseSnapshot: receiptPricingSnapshot(replacement),
      packageItemPriceSnapshot: replacement.packageItemPriceSnapshot,
      planId: replacement.planId,
      planDiscountSnapshot: replacement.planDiscountSnapshot,
      stock: replacement.stock ?? 0,
      remark: item.remark,
      sourceType: 'receipt',
      sourceId: share.id,
      sourceName: `客户替换：${trustedBase.sourceName}`,
      replacementDeviceId: undefined,
    };
    return {
      selectionKind: 'replaced',
      effectiveItem,
      originalItemSnapshot,
      disposition: 'pending' as const,
      replacementSource: {
        requestedDeviceId: replacement.deviceId ?? '',
        originalDeviceId: trustedBase.deviceId,
        originalItemId: trustedBase.itemId,
      },
    };
  });
  const removed = share.id.startsWith('LINK-')
    ? (share.itemsSnapshot ?? []).flatMap((original) => {
        if (seen.has(original.itemId)) return [];
        if (!isReceiptCapableShareItem(original)) {
          throw new Error('公开通道未冻结完整选配来源，禁止从目录补齐');
        }
        return [
          {
            selectionKind: 'removed' as const,
            effectiveItem: null,
            originalItemSnapshot: structuredClone(original),
            disposition: 'pending' as const,
          } satisfies ReceiptSelectionItemSnapshot,
        ];
      })
    : [];
  if (selected.length === 0 && removed.length === 0)
    throw new Error('回执必须包含冻结选择或明确删除');
  return [...selected, ...removed];
}

function findQuoteEntry(
  state: CpqState,
  quoteId: string,
  version: number,
): [string, Quote] | undefined {
  const matches = Object.entries(state.quotes).filter(
    ([, quote]) => quote.quoteId === quoteId && quote.version === version,
  );
  return matches.length === 1 ? matches[0] : undefined;
}

function requireReceipt(state: CpqState, receiptId: string): ReceiptRecord {
  const receipt = state.receipts.find((item) => item.id === receiptId);
  if (!receipt) throw new Error(`回执 ${receiptId} 不存在`);
  return receipt;
}

function bindingForReceipt(
  state: CpqState,
  receipt: ReceiptRecord,
): ReceiptBinding {
  if (receipt.publicationId || receipt.linkId || receipt.quoteMasterId) {
    const publication = receipt.publicationId
      ? state.publications?.find((item) => item.id === receipt.publicationId)
      : undefined;
    const link = receipt.linkId
      ? state.negotiationLinks?.find((item) => item.id === receipt.linkId)
      : undefined;
    const sourceQuoteEntry = findQuoteEntry(
      state,
      receipt.sourceQuoteId,
      receipt.sourceVersion,
    );
    const sourceQuoteKey = sourceQuoteEntry?.[0];
    const sourceQuote = sourceQuoteEntry?.[1];
    return {
      receipt,
      // legacy property is only used by old UI; exact v5 validation is below.
      share: link
        ? {
            id: link.id,
            token: link.token,
            quoteId: receipt.sourceQuoteId,
            quoteVersion: receipt.sourceVersion,
            allowSelection: link.allowSelection,
            status: 'active',
            createdAt: link.createdAt,
          }
        : undefined,
      sourceQuoteKey,
      sourceQuote,
      valid: Boolean(
        publication &&
          link &&
          sourceQuoteKey &&
          sourceQuote &&
          publication.linkId === link.id &&
          publication.quoteMasterId === receipt.quoteMasterId &&
          publication.quoteId === receipt.sourceQuoteId &&
          publication.quoteVersion === receipt.sourceVersion &&
          publication.customerSnapshot.customerId === receipt.customerId &&
          link.customerId === receipt.customerId &&
          quoteMasterId(sourceQuote) === receipt.quoteMasterId,
      ),
    };
  }
  const share = state.shares.find((item) => item.id === receipt.shareId);
  const sourceQuoteEntry = findQuoteEntry(
    state,
    receipt.sourceQuoteId,
    receipt.sourceVersion,
  );
  const sourceQuoteKey = sourceQuoteEntry?.[0];
  const sourceQuote = sourceQuoteEntry?.[1];
  return {
    receipt,
    share,
    sourceQuoteKey,
    sourceQuote,
    valid: Boolean(
      share &&
        sourceQuoteKey &&
        sourceQuote &&
        share.quoteId === receipt.sourceQuoteId &&
        share.quoteVersion === receipt.sourceVersion,
    ),
  };
}

function requireValidBinding(
  state: CpqState,
  receipt: ReceiptRecord,
): Required<ReceiptBinding> {
  const binding = bindingForReceipt(state, receipt);
  if (
    !binding.valid ||
    !binding.share ||
    !binding.sourceQuoteKey ||
    !binding.sourceQuote
  ) {
    throw new Error(
      `回执 ${receipt.id} 的分享、来源报价或来源版本不匹配，禁止转换`,
    );
  }
  return binding as Required<ReceiptBinding>;
}

function assertReceiptPermission(
  state: CpqState,
  receipt: ReceiptRecord,
  actionId: 'quotes:quote.read' | 'quotes:quote.edit',
): Required<ReceiptBinding> {
  const binding = requireValidBinding(state, receipt);
  const roleId = activeRoleIdForState(state);
  if (
    !roleId ||
    !resolveDisposablePermissionAction(
      state,
      roleId,
      'quotes',
      actionId,
      // Receipt adoption creates a new internal draft; it never edits the
      // frozen submitted/publication source in place.
      'draft',
      quotePermissionContext(state, binding.sourceQuote),
    )
  )
    throw new Error(
      actionId === 'quotes:quote.edit'
        ? '当前成员无权转换此回执'
        : '当前成员无权查看此回执',
    );
  return binding;
}

function requireConversionTarget(
  state: CpqState,
  receipt: ReceiptRecord,
): Quote {
  const target = receipt.convertedQuoteId
    ? state.quotes[receipt.convertedQuoteId]
    : undefined;
  if (
    receipt.status !== 'converted' ||
    !target ||
    target.quoteId !== receipt.convertedQuoteId ||
    target.sourceReceiptId !== receipt.id ||
    target.sourceQuoteId !== receipt.sourceQuoteId ||
    target.sourceVersion !== receipt.sourceVersion
  ) {
    throw new Error(`回执 ${receipt.id} 的转换目标不存在或来源不匹配`);
  }
  return target;
}

function hasConflictingSalesRevision(
  state: CpqState,
  receipt: ReceiptRecord,
): boolean {
  return Object.values(state.quotes).some(
    (candidate) =>
      candidate.sourceQuoteId === receipt.sourceQuoteId &&
      candidate.sourceVersion === receipt.sourceVersion &&
      candidate.sourceReceiptId !== receipt.id,
  );
}

function openQuote(state: CpqState, quote: Quote): CpqState {
  const next = structuredClone(state);
  next.activeView = 'workspace';
  next.activeQuoteId = quote.quoteId;
  if (!next.openQuoteIds.includes(quote.quoteId)) {
    next.openQuoteIds.push(quote.quoteId);
  }
  return next;
}

export function receiptAccess(state: CpqState): ReceiptAccess {
  const roleId = activeRoleIdForState(state);
  const canForQuote = (quote: Quote, actionId: string) =>
    Boolean(
      roleId &&
        resolveDisposablePermissionAction(
          state,
          roleId,
          'quotes',
          actionId,
          'draft',
          quotePermissionContext(state, quote),
        ),
    );
  const quoteValues = Object.values(state.quotes);
  const canView = quoteValues.some((quote) =>
    canForQuote(quote, 'quotes:quote.read'),
  );
  return {
    canView,
    canConvert: quoteValues.some((quote) =>
      canForQuote(quote, 'quotes:quote.edit'),
    ),
  };
}

export function isReceiptProcessed(
  state: CpqState,
  receipt: ReceiptRecord,
): boolean {
  if (!bindingForReceipt(state, receipt).valid) {
    return false;
  }
  if (
    receipt.disposition === 'customer_confirmed' ||
    receipt.disposition === 'reply_no_change' ||
    receipt.disposition === 'return_for_information'
  )
    return true;
  if (receipt.status !== 'converted') return false;
  try {
    const target = requireConversionTarget(state, receipt);
    return Boolean(
      receipt.disposition === 'adopted' ||
        state.publications?.some(
          (publication) =>
            publication.quoteId === target.quoteId &&
            publication.quoteVersion === target.version,
        ),
    );
  } catch {
    return false;
  }
}

/**
 * The receipt panel may span every immutable publication in one quote master.
 * Keep historical and otherwise unsafe receipts visible for audit, but do not
 * expose an import action that is guaranteed to fail in the state transition.
 */
export function isReceiptImportable(
  state: CpqState,
  receipt: ReceiptRecord,
): boolean {
  if (
    state.receipts.filter((candidate) => candidate.id === receipt.id).length !==
    1
  ) {
    return false;
  }
  const binding = bindingForReceipt(state, receipt);
  if (!binding.valid) {
    return false;
  }
  if (receipt.status === 'converted') {
    try {
      requireConversionTarget(state, receipt);
      return true;
    } catch {
      return false;
    }
  }
  if (receipt.selectionSnapshot.kind !== 'structured') {
    return false;
  }
  if (
    receipt.status !== 'submitted' ||
    receipt.disposition ||
    (receipt.publicationId &&
      receipt.customerAction !== 'request_change' &&
      receipt.customerAction !== 'accept')
  ) {
    return false;
  }
  if (receipt.publicationId) {
    const { publication, link } = publicationSource(
      state,
      receipt.publicationId,
    );
    if (!publication || !link || link.currentPublicationId !== publication.id) {
      return false;
    }
    if (hasConflictingSalesRevision(state, receipt)) {
      return false;
    }
  }
  return true;
}

export function receiptsForQuote(
  state: CpqState,
  quote: Quote,
): ReceiptRecord[] {
  const roleId = activeRoleIdForState(state);
  if (
    !roleId ||
    !resolveDisposablePermissionAction(
      state,
      roleId,
      'quotes',
      'quotes:quote.read',
      'draft',
      quotePermissionContext(state, quote),
    )
  )
    return [];
  return state.receipts.filter((receipt) => {
    const binding = bindingForReceipt(state, receipt);
    return (
      binding.valid &&
      receipt.sourceQuoteId === quote.quoteId &&
      receipt.sourceVersion === quote.version
    );
  });
}

export function pendingReceiptsForQuote(
  state: CpqState,
  quote: Quote,
): ReceiptRecord[] {
  return receiptsForQuote(state, quote).filter(
    (receipt) => !isReceiptProcessed(state, receipt),
  );
}

export function pendingReceiptCount(state: CpqState): number {
  const readableIds = new Set(
    Object.values(state.quotes).flatMap((quote) =>
      receiptsForQuote(state, quote).map((receipt) => receipt.id),
    ),
  );
  return state.receipts.filter(
    (receipt) =>
      readableIds.has(receipt.id) && !isReceiptProcessed(state, receipt),
  ).length;
}

/** Navigation badge: count quote masters with updates, not receipt rows. */
export function pendingReceiptQuoteCount(state: CpqState): number {
  const masterIds = new Set<string>();
  for (const quote of Object.values(state.quotes)) {
    if (pendingReceiptsForQuote(state, quote).length > 0) {
      masterIds.add(quoteMasterId(quote));
    }
  }
  return masterIds.size;
}

export function orphanReceipts(state: CpqState): ReceiptRecord[] {
  // Orphans have no provable resource context and therefore remain diagnostic
  // read-only facts. All mutation paths still reject them via exact binding.
  if (!receiptAccess(state).canView) return [];
  return state.receipts.filter(
    (receipt) => !bindingForReceipt(state, receipt).valid,
  );
}

export function markReceiptOpened(
  state: CpqState,
  receiptId: string,
  openedAt = new Date().toISOString(),
): CpqState {
  const receipt = requireReceipt(state, receiptId);
  assertReceiptPermission(state, receipt, 'quotes:quote.read');
  if (receipt.openedAt) return state;
  const next = structuredClone(state);
  const current = requireReceipt(next, receiptId);
  current.openedAt = openedAt;
  return next;
}

/** Close a customer request without creating or mutating any quote version. */
export function closeReceiptWithoutQuoteChange(
  state: CpqState,
  receiptId: string,
  note = '明确不采纳客户要求',
): CpqState {
  const receipt = requireReceipt(state, receiptId);
  if (isReceiptProcessed(state, receipt)) return state;
  if (receipt.status !== 'submitted') {
    throw new Error('已导入报价工作台的反馈不能直接标记为不采纳');
  }
  assertReceiptPermission(state, receipt, 'quotes:quote.edit');
  const next = structuredClone(state);
  const current = requireReceipt(next, receiptId);
  current.disposition = 'reply_no_change';
  current.dispositionAt = new Date().toISOString();
  current.dispositionNote = note;
  return next;
}

export function openConvertedReceipt(
  state: CpqState,
  receiptId: string,
): CpqState {
  const receipt = requireReceipt(state, receiptId);
  assertReceiptPermission(state, receipt, 'quotes:quote.edit');
  const target = requireConversionTarget(state, receipt);
  return openQuote(state, target);
}

export function convertReceiptToQuoteVersion(
  state: CpqState,
  receiptId: string,
  fieldValuePort?: UnifiedFieldValuePort,
): CpqState {
  const receipt = requireReceipt(state, receiptId);
  if (receipt.publicationId && receipt.customerAction === 'accept') {
    throw new Error('客户已确认当前分享版本，无需创建更新版本');
  }
  const { sourceQuote, sourceQuoteKey } = assertReceiptPermission(
    state,
    receipt,
    'quotes:quote.edit',
  );
  if (receipt.status === 'converted') {
    return openQuote(state, requireConversionTarget(state, receipt));
  }
  if (receipt.selectionSnapshot.kind !== 'structured') {
    throw new Error(`回执 ${receipt.id} 缺少可恢复的结构化选择，禁止转换`);
  }
  if (receipt.publicationId && hasConflictingSalesRevision(state, receipt)) {
    throw new Error('销售已修改客户提交基线，必须人工处理冲突');
  }
  const adoptedItems = receipt.selectionSnapshot.items.filter(
    (item) => item.disposition !== 'rejected',
  );
  if (receipt.publicationId && !adoptedItems.length && !receipt.note.trim()) {
    throw new Error('没有已采纳的设备变更或备注，不能生成草稿');
  }
  const next = createQuoteWorkflowVersion(state, sourceQuoteKey, {
    originTab: 'feedback',
    generationSource: 'customer_feedback',
    generationReason: '采纳客户反馈',
    sourceReceiptId: receipt.id,
  });
  const target = next.quotes[next.activeQuoteId];
  let appliedItems = receipt.publicationId
    ? structuredClone(sourceQuote.items)
    : receipt.selectionSnapshot.items.flatMap((item) =>
        item.effectiveItem ? [structuredClone(item.effectiveItem)] : [],
      );
  for (const item of receipt.publicationId ? adoptedItems : []) {
    const originalId =
      item.originalItemSnapshot?.itemId ?? item.effectiveItem?.itemId;
    if (item.selectionKind === 'removed' && originalId) {
      appliedItems = appliedItems.filter(
        (candidate) => candidate.itemId !== originalId,
      );
    } else if (item.selectionKind === 'added' && item.effectiveItem) {
      appliedItems = [...appliedItems, structuredClone(item.effectiveItem)];
    } else if (item.effectiveItem && originalId) {
      const effectiveItem = item.effectiveItem;
      appliedItems = appliedItems.map((candidate) =>
        candidate.itemId === originalId
          ? item.selectionKind === 'kept'
            ? {
                ...candidate,
                quantity: effectiveItem.quantity,
                remark: effectiveItem.remark,
              }
            : structuredClone(effectiveItem)
          : candidate,
      );
    }
  }
  target.items = appliedItems;
  target.overallDiscount = receipt.selectionSnapshot.overallDiscountSnapshot;
  if (fieldValuePort) {
    next.quotes[next.activeQuoteId] = freezeSelectedCalculationFacts(
      next,
      target,
      fieldValuePort,
    );
  }
  const currentReceipt = requireReceipt(next, receipt.id);
  currentReceipt.status = 'converted';
  currentReceipt.convertedQuoteId = next.activeQuoteId;
  next.activeView = 'workspace';
  return next;
}

/** 客户回执进入销售工作台；重复导入只重开原目标，不重复创建版本。 */
export function importReceiptToQuoteWorkspace(
  state: CpqState,
  receiptId: string,
  fieldValuePort?: UnifiedFieldValuePort,
): CpqState {
  return convertReceiptToQuoteVersion(state, receiptId, fieldValuePort);
}

/** Applies only explicit adopted rows; publishing remains a separate sales action. */
export function applyReceiptChanges(
  state: CpqState,
  receiptId: string,
): CpqState {
  const receipt = requireReceipt(state, receiptId);
  if (receipt.selectionSnapshot.kind !== 'structured')
    return convertReceiptToQuoteVersion(state, receiptId);
  const next = structuredClone(state);
  const current = requireReceipt(next, receiptId);
  if (current.selectionSnapshot.kind === 'structured') {
    current.selectionSnapshot.items = current.selectionSnapshot.items.map(
      (item) =>
        item.disposition === 'pending'
          ? { ...item, disposition: 'rejected' }
          : item,
    );
  }
  return convertReceiptToQuoteVersion(next, receiptId);
}

export function submitShareReceipt(
  state: CpqState,
  input: SubmitShareReceiptInput,
): CpqState {
  if (input.linkId || input.publicationId) {
    if (!input.linkId || !input.publicationId)
      throw new Error('回执必须同时绑定链接和发布');
    const { publication, link } = publicationSource(state, input.publicationId);
    if (!publication || !link || link.id !== input.linkId)
      throw new Error('链接或发布不存在或错绑');
    if (
      link.lifecycle !== 'active' ||
      link.currentPublicationId !== publication.id
    )
      throw new Error('链接不可提交或发布已过旧');
    if (negotiationIsExpired(link, publication)) throw new Error('链接已过期');
    if (
      link.customerId !== publication.customerSnapshot.customerId ||
      input.companyOrName !== publication.customerSnapshot.name ||
      input.phone !== publication.customerSnapshot.phone
    )
      throw new Error('客户身份与固定链接不匹配');
    if (state.receipts.some((item) => item.publicationId === publication.id))
      return state;
    if (!input.items) throw new Error('回执缺少结构化选择');
    if (publication.snapshotPolicy !== 'published-channel-v1') {
      throw new Error('发布缺少已批准通道快照，不能提交回执');
    }
    const syntheticShare: ShareRecord = {
      id: link.id,
      token: link.token,
      quoteId: publication.quoteId,
      quoteVersion: publication.quoteVersion,
      allowSelection: publication.allowSelection,
      snapshotPolicy: publication.snapshotPolicy,
      customerSnapshot: structuredClone(publication.customerSnapshot),
      validUntilSnapshot: publication.validUntilSnapshot,
      itemsSnapshot: publication.itemsSnapshot.map((item) => ({ ...item })),
      overallDiscountSnapshot: publication.overallDiscountSnapshot,
      selectionOptionsSnapshot: publication.selectionOptionsSnapshot.map(
        (item) => ({ ...item }),
      ),
      status: 'active',
      createdAt: publication.createdAt,
    };
    const frozenItems = freezeSelectionItems(
      state,
      syntheticShare,
      input.items,
    );
    const amounts = quoteAmounts({
      items: frozenItems.flatMap((item) =>
        item.effectiveItem ? [item.effectiveItem] : [],
      ),
      overallDiscount: publication.overallDiscountSnapshot,
    });
    const next = structuredClone(state);
    const createdAt = input.createdAt ?? new Date().toISOString();
    next.receipts.unshift({
      id: `RECEIPT-${String(next.nextSequence).padStart(4, '0')}`,
      shareId: link.id,
      sourceQuoteId: publication.quoteId,
      sourceVersion: publication.quoteVersion,
      customerId: publication.customerSnapshot.customerId,
      quoteMasterId: publication.quoteMasterId,
      linkId: link.id,
      publicationId: publication.id,
      customerAction: input.customerAction ?? 'request_change',
      customerIdentity: `${input.companyOrName}|${input.phone}`,
      companyOrName: input.companyOrName,
      phone: input.phone,
      note: input.note,
      selectionSnapshot: {
        kind: 'structured',
        items: frozenItems,
        overallDiscountSnapshot: publication.overallDiscountSnapshot,
        subtotalSnapshot: amounts.subtotal,
        totalSnapshot: amounts.total,
        submittedAt: createdAt,
      },
      status: 'submitted',
      createdAt,
      ...(input.customerAction === 'accept'
        ? {
            disposition: 'customer_confirmed' as const,
            dispositionAt: createdAt,
            dispositionNote: '客户已确认当前分享版本',
          }
        : {}),
    });
    next.negotiationDrafts = next.negotiationDrafts.filter(
      (draft) =>
        draft.linkId !== link.id || draft.publicationId !== publication.id,
    );
    next.nextSequence += 1;
    return next;
  }
  const share = state.shares.find((item) => item.id === input.shareId);
  if (!share) throw new Error('分享不存在或已失效');
  const legacyMaster = state.quotes[share.quoteId]?.masterId ?? share.quoteId;
  if (
    state.negotiationLinks?.some((link) => link.quoteMasterId === legacyMaster)
  ) {
    throw new Error('旧分享已迁移到固定协商链接，禁止通过旧来源提交回执');
  }
  if (share.snapshotPolicy !== 'published-channel-v1') {
    throw new Error('分享缺少已发布通道快照，不能提交回执');
  }
  const existing = state.receipts.find((item) => item.shareId === share.id);
  if (existing) return state;
  if (share.status === 'revoked') throw new Error('分享已撤销，不能提交回执');
  if (share.status !== 'active') throw new Error('分享已失效，不能提交回执');
  const source = findQuoteEntry(state, share.quoteId, share.quoteVersion);
  if (!source) throw new Error('分享绑定的报价版本不存在，不能提交回执');
  if (!share.itemsSnapshot) {
    throw new Error('分享缺少冻结选配快照，不能提交结构化回执');
  }
  if (!input.items) {
    throw new Error('分享回执缺少结构化选择，禁止从 note 恢复');
  }
  if (
    !share.itemsSnapshot.every(isReceiptCapableShareItem) ||
    !input.items.every(isReceiptCapableShareItem)
  ) {
    throw new Error('公开通道未冻结完整选配来源，不能提交结构化回执');
  }
  const overallDiscount = share.overallDiscountSnapshot;
  if (
    typeof overallDiscount !== 'number' ||
    !Number.isFinite(overallDiscount) ||
    overallDiscount < 0
  ) {
    throw new Error('分享冻结整单折扣无效');
  }

  const createdAt = input.createdAt ?? new Date().toISOString();
  const frozenItems = freezeSelectionItems(
    state,
    share,
    input.items.filter(isReceiptCapableShareItem),
  );
  const effectiveItems = frozenItems.flatMap((item) =>
    item.effectiveItem ? [item.effectiveItem] : [],
  );
  const amounts = quoteAmounts({
    items: effectiveItems,
    overallDiscount,
  });
  const next = structuredClone(state);
  const receipt: ReceiptRecord = {
    id: `RECEIPT-${String(next.nextSequence).padStart(4, '0')}`,
    shareId: share.id,
    sourceQuoteId: share.quoteId,
    sourceVersion: share.quoteVersion,
    customerAction: input.customerAction ?? 'request_change',
    ...(share.customerSnapshot?.customerId
      ? { customerId: share.customerSnapshot.customerId }
      : {}),
    companyOrName: input.companyOrName,
    phone: input.phone,
    note: input.note,
    selectionSnapshot: {
      kind: 'structured',
      items: frozenItems,
      overallDiscountSnapshot: overallDiscount,
      subtotalSnapshot: amounts.subtotal,
      totalSnapshot: amounts.total,
      submittedAt: createdAt,
    },
    status: 'submitted',
    createdAt,
    ...(input.customerAction === 'accept'
      ? {
          disposition: 'customer_confirmed',
          dispositionAt: createdAt,
          dispositionNote: '客户已确认当前分享版本',
        }
      : {}),
  };
  next.receipts.unshift(receipt);
  next.shares = next.shares.map((item) =>
    item.id === share.id ? { ...item, status: 'receipt_received' } : item,
  );
  next.audits.unshift({
    id: `AUD-${String(next.nextSequence + 1).padStart(4, '0')}`,
    quoteId: share.quoteId,
    quoteVersion: share.quoteVersion,
    action: 'receipt.submitted',
    result: 'success',
    detail: `客户回执 ${receipt.id} 已绑定来源版本`,
    createdAt,
  });
  next.nextSequence += 2;
  return next;
}
